import { Component, inject, OnInit, AfterViewInit, computed } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Sidebar } from './shared/sidebar/sidebar';
import { Header } from './shared/header/header';
import { GlobalLoaderComponent } from './shared/global-loader/global-loader';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart, ResolveStart, ResolveEnd, NavigationCancel, NavigationError } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoaderService } from './core/services/loader.service';
import { Store } from '@ngrx/store';
import * as AuthActions from './store/auth/auth.actions';
import { MaintenanceCheckService } from './core/services/maintenance-check.service';
import { MaintenancePageComponent } from './shared/maintenance-page/maintenance-page';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, Sidebar, Header, CommonModule, GlobalLoaderComponent, MaintenancePageComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.css'],
})
export class App implements OnInit, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private loader = inject(LoaderService);
  private store = inject(Store);
  private maintenanceCheckService = inject(MaintenanceCheckService);

  // Maintenance state from service (populated by APP_INITIALIZER before bootstrap)
  isUnderMaintenance = computed(() => this.maintenanceCheckService.isUnderMaintenance());
  maintenanceEndTime = computed(() => this.maintenanceCheckService.maintenanceEndTime());

  private routeData$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    startWith(null),
    map(() => {
      let r = this.route;
      while (r.firstChild) r = r.firstChild;
      return r.snapshot.data;
    })
  );

  hideHeader = toSignal(this.routeData$.pipe(map(d => !!d['hideHeader'])), { initialValue: false });
  hideSidebar = toSignal(this.routeData$.pipe(map(d => !!d['hideSidebar'])), { initialValue: false });
  hideLoader = toSignal(this.routeData$.pipe(map(d => !!d['hideLoader'])), { initialValue: false });

  ngOnInit(): void {
    // Restore auth state from localStorage on app init (for cross-tab persistence)
    this.store.dispatch(AuthActions.restoreAuth());

    // Show loader on navigation start
    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationStart || evt instanceof ResolveStart) {
        this.loader.show();
      }

      if (evt instanceof NavigationEnd || evt instanceof ResolveEnd ||
          evt instanceof NavigationCancel || evt instanceof NavigationError) {
        // Hide loader when navigation completes, is cancelled (guard redirect), or errors
        this.loader.hide();
        // Also hide the initial HTML loader (from index.html)
        this.hideInitialLoader();
      }
    });
  }

  ngAfterViewInit(): void {
    // Hide initial HTML loader once app is rendered
    this.hideInitialLoader();
  }

  /** Hide the initial HTML loader */
  private hideInitialLoader(): void {
    const loader = document.getElementById('app-initial-loader');
    if (loader) {
      loader.classList.add('hidden');
      setTimeout(() => loader.remove(), 200);
    }
  }
}
